import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  FileText, 
  Download, 
  Loader2, 
  Filter,
  BarChart3,
  FileSpreadsheet,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Reports = () => {
  const { token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  // Filter states
  const [projects, setProjects] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [disciplines, setDisciplines] = useState([]);
  const [approvalCodes, setApprovalCodes] = useState([]);
  const [statusCodes, setStatusCodes] = useState([]);
  
  // Selected filters
  const [selectedProject, setSelectedProject] = useState('all');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedDiscipline, setSelectedDiscipline] = useState('all');
  const [selectedApprovalCode, setSelectedApprovalCode] = useState('all');
  const [selectedStatusCode, setSelectedStatusCode] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data.documents);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const fetchFilterOptions = useCallback(async () => {
    try {
      const [projectsRes, typesRes, disciplinesRes, approvalRes, statusCodesRes] = await Promise.all([
        axios.get(`${API_URL}/api/projects`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/document-types`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/disciplines`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/approval-codes`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/status-codes`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProjects(projectsRes.data.projects);
      setDocumentTypes(typesRes.data.document_types);
      setDisciplines(disciplinesRes.data.disciplines);
      setApprovalCodes(approvalRes.data.approval_codes);
      setStatusCodes(statusCodesRes.data.status_codes);
    } catch (error) {
      console.error('Failed to load filter options');
    }
  }, [token]);

  useEffect(() => {
    fetchDocuments();
    fetchFilterOptions();
  }, [fetchDocuments, fetchFilterOptions]);

  // Filter documents based on selected filters
  const filteredDocuments = documents.filter(doc => {
    if (selectedProject !== 'all' && doc.project_id !== selectedProject) return false;
    if (selectedType !== 'all' && doc.document_type_id !== selectedType) return false;
    if (selectedDiscipline !== 'all' && doc.discipline_id !== selectedDiscipline) return false;
    if (selectedApprovalCode !== 'all' && doc.approval_code_id !== selectedApprovalCode) return false;
    if (selectedStatusCode !== 'all' && doc.status_code_id !== selectedStatusCode) return false;
    if (selectedStatus !== 'all' && doc.status !== selectedStatus) return false;
    return true;
  });

  // Export to CSV
  const exportToCSV = () => {
    setExporting(true);
    try {
      // Define CSV columns
      const headers = [
        'Document Number',
        'Name',
        'Description',
        'Project',
        'Document Type',
        'Revision Code',
        'Discipline',
        'Approval Code',
        'Status Code',
        'Workflow Status',
        'Version',
        'File Type',
        'File Size (bytes)',
        'Tags',
        'Creator',
        'Created At',
        'Updated At'
      ];

      // Build CSV rows
      const rows = filteredDocuments.map(doc => {
        const project = projects.find(p => p.id === doc.project_id);
        return [
          doc.document_number || '',
          doc.name || '',
          (doc.description || '').replace(/"/g, '""'), // Escape quotes
          project?.name || '',
          doc.document_type ? `${doc.document_type.code} - ${doc.document_type.name}` : '',
          doc.revision_code ? `${doc.revision_code.code} - ${doc.revision_code.name}` : '',
          doc.discipline ? `${doc.discipline.code} - ${doc.discipline.name}` : '',
          doc.approval_code ? `${doc.approval_code.code} - ${doc.approval_code.name}` : '',
          doc.status_code ? `${doc.status_code.code} - ${doc.status_code.name}` : '',
          doc.status || '',
          doc.version || '',
          doc.file_type || '',
          doc.file_size || '',
          (doc.tags || []).join('; '),
          doc.created_by_name || '',
          doc.created_at ? format(new Date(doc.created_at), 'yyyy-MM-dd HH:mm:ss') : '',
          doc.updated_at ? format(new Date(doc.updated_at), 'yyyy-MM-dd HH:mm:ss') : ''
        ];
      });

      // Convert to CSV string
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `documents_report_${format(new Date(), 'yyyyMMdd_HHmmss')}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`Exported ${filteredDocuments.length} documents to CSV`);
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const resetFilters = () => {
    setSelectedProject('all');
    setSelectedType('all');
    setSelectedDiscipline('all');
    setSelectedApprovalCode('all');
    setSelectedStatusCode('all');
    setSelectedStatus('all');
  };

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-gray-500/10 text-gray-600',
      pending_approval: 'bg-yellow-500/10 text-yellow-600',
      approved: 'bg-green-500/10 text-green-600',
      rejected: 'bg-red-500/10 text-red-600',
      archived: 'bg-blue-500/10 text-blue-600'
    };
    return <Badge className={styles[status] || 'bg-gray-500/10 text-gray-600'}>{status?.replace('_', ' ')}</Badge>;
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Reports</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Generate and export reports from your document management system
          </p>
        </div>
      </div>

      {/* Document Report Card */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <FileSpreadsheet className="w-5 h-5 text-primary" />
              </div>
              <div>
                <CardTitle>Document Report</CardTitle>
                <CardDescription>
                  Export all documents with metadata to CSV
                </CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={resetFilters}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset Filters
              </Button>
              <Button onClick={exportToCSV} disabled={exporting || filteredDocuments.length === 0} data-testid="export-csv-btn">
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export CSV ({filteredDocuments.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6 p-4 bg-muted/30 rounded-lg">
            <div className="space-y-2">
              <Label className="text-xs">Project</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Projects" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Document Type</Label>
              <Select value={selectedType} onValueChange={setSelectedType}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {documentTypes.map(dt => (
                    <SelectItem key={dt.id} value={dt.id}>{dt.code} - {dt.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Discipline</Label>
              <Select value={selectedDiscipline} onValueChange={setSelectedDiscipline}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Disciplines" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Disciplines</SelectItem>
                  {disciplines.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.code} - {d.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Approval Code</Label>
              <Select value={selectedApprovalCode} onValueChange={setSelectedApprovalCode}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Codes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Codes</SelectItem>
                  {approvalCodes.map(ac => (
                    <SelectItem key={ac.id} value={ac.id}>{ac.code} - {ac.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Status Code</Label>
              <Select value={selectedStatusCode} onValueChange={setSelectedStatusCode}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Codes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Codes</SelectItem>
                  {statusCodes.map(sc => (
                    <SelectItem key={sc.id} value={sc.id}>{sc.code} - {sc.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Workflow Status</Label>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <SelectTrigger className="h-9">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                  <SelectItem value="pending_approval">Pending Approval</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results Preview */}
          <div className="border rounded-lg">
            <div className="p-3 bg-muted/50 border-b flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <FileText className="w-4 h-4" />
                <span>Preview ({filteredDocuments.length} documents)</span>
              </div>
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            </div>
            
            <div className="max-h-[400px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="sticky top-0 bg-background">Doc Number</TableHead>
                    <TableHead className="sticky top-0 bg-background">Name</TableHead>
                    <TableHead className="sticky top-0 bg-background">Type</TableHead>
                    <TableHead className="sticky top-0 bg-background">Discipline</TableHead>
                    <TableHead className="sticky top-0 bg-background">Approval</TableHead>
                    <TableHead className="sticky top-0 bg-background">Status</TableHead>
                    <TableHead className="sticky top-0 bg-background">Created</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocuments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                        {loading ? 'Loading documents...' : 'No documents match the selected filters'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredDocuments.slice(0, 100).map(doc => (
                      <TableRow key={doc.id}>
                        <TableCell className="font-mono text-sm">{doc.document_number || '-'}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{doc.name}</TableCell>
                        <TableCell>
                          {doc.document_type ? (
                            <Badge variant="outline" className="text-xs">{doc.document_type.code}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {doc.discipline ? (
                            <Badge variant="outline" className="text-xs">{doc.discipline.code}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>
                          {doc.approval_code ? (
                            <Badge variant="outline" className="text-xs bg-green-500/10">{doc.approval_code.code}</Badge>
                          ) : '-'}
                        </TableCell>
                        <TableCell>{getStatusBadge(doc.status)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {doc.created_at ? format(new Date(doc.created_at), 'MMM d, yyyy') : '-'}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
            
            {filteredDocuments.length > 100 && (
              <div className="p-3 bg-muted/50 border-t text-center text-sm text-muted-foreground">
                Showing first 100 of {filteredDocuments.length} documents. Export to see all.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Reports;
